import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { headingId } from '../utils/wikiHeadings';

// markdown 渲染的唯一实现。四个调用方：worklog 抽屉、/todo 右栏的留言、任务备注，
// 以及 wiki 正文。把这串类名复制到第二处，两边的样式当天就开始漂 ——
// 所以它在这里，不在 JSX 里。
//
// 没装 @tailwindcss/typography，没有 prose 类可用；用 arbitrary-variant 手写
// 常见元素的间距，换来不用多引入一个构建依赖（这个取舍沿用 WorklogDrawer 原来
// 的做法）。
//
// ⚠️ 安全约束：刻意不装 rehype-raw，所以正文里的裸 HTML（<script>、
// <img onerror=...>）会被当纯文本输出。谁要给这个组件「加个 HTML 支持」，
// 那一刻就是把用户正文变成 XSS 载荷 —— 不要加。

// 两套字号共用的部分。
//
// ⚠️ 这里只放**与字号无关**的声明：颜色、列表符号、溢出行为、显示模式。
// 任何一个尺寸量（padding / margin / font-size / 圆角）都不放这儿，一律下沉到
// 两个 SIZES 里各写一次。
//
// 原来不是这样的：尺寸量写在 BASE 里，article 靠「重复声明 + 赌 Tailwind 的
// 生成顺序」去盖（那条注释还在下面）。wiki 改版要盖掉的尺寸从 1 个涨到 10 个，
// 而生成顺序对 `rounded` vs `rounded-lg`、`px-2` vs `px-2.5` 这种同族工具类
// 谁在前谁在后并没有承诺 —— 赌一次能过，赌十次一定有一次是反的，而且坏掉的
// 表现是「某个元素在某一套字号下间距不对」，没有任何断言会红。
//
// 下沉不是「把类名复制成第二份」：每个工具类在每套字号里只出现一次，两套的值
// 本来就该不同。真正要避免的是同一个值被写两遍。
const BASE = [
  '[&_p:first-child]:mt-0 [&_p:last-child]:mb-0',
  '[&_ul]:list-disc [&_ol]:list-decimal',
  '[&_code]:bg-todo-card2 [&_code]:rounded',
  '[&_pre]:bg-todo-card2 [&_pre]:overflow-x-auto',
  '[&_pre_code]:bg-transparent [&_pre_code]:p-0',
  '[&_blockquote]:border-l-todo-line2',
  '[&_a]:text-todo-accent',
  // 留言里粘进来的截图。没有 max-w-full 会把 300px 宽的右栏撑破。
  '[&_img]:max-w-full [&_img]:rounded-md',
  // GFM 表格。block + overflow-x-auto 让宽表在窄栏里自己横滚，而不是撑破容器。
  '[&_table]:block [&_table]:overflow-x-auto',
  '[&_th]:border [&_th]:border-todo-line [&_th]:bg-todo-card2 [&_th]:text-left',
  '[&_td]:border [&_td]:border-todo-line',
  '[&_hr]:border-todo-line',
  '[&_del]:text-todo-muted',
].join(' ');

// 两套字号。compact 是原来那一套 —— 它是给 300px 宽的右栏调的，四个现存调用方
// 都靠它，**每个值都与改版前逐字相同**（只是从 BASE 挪了下来）。
//
// article 给 wiki 正文：那是页面主体，行宽 720px（见 WikiLayout / 设计稿的
// `.doc`）。这一套翻自 docs/design/wiki-ui-mockup.html 的 `.body` —— 16px/1.75，
// 比改版前的 15px/1.75 再大一档，标题层级也整体上抬。
//
// 为什么必须是一个 prop 而不是让调用方传 className 覆盖：外面传进来的类与
// 这里的 arbitrary variant（[&_h2]:text-base）**特异性相同**，
// Tailwind 的胜负由生成的 CSS 源码顺序决定、不由 class 属性里的先后决定 ——
// 所以传 className 改得动基础字号，改不动标题层级。
const SIZES = {
  compact: [
    'text-sm leading-relaxed',
    '[&_h1]:text-lg [&_h1]:font-bold [&_h1]:mt-4 [&_h1]:mb-2',
    '[&_h2]:text-base [&_h2]:font-semibold [&_h2]:mt-4 [&_h2]:mb-2',
    '[&_h3]:text-sm [&_h3]:font-semibold [&_h3]:mt-3 [&_h3]:mb-1.5',
    '[&_p]:my-2',
    '[&_ul]:pl-5 [&_ol]:pl-5 [&_li]:my-0.5',
    '[&_code]:px-1',
    '[&_pre]:p-3 [&_pre]:rounded',
    '[&_blockquote]:border-l-2 [&_blockquote]:pl-3 [&_blockquote]:text-todo-muted',
    '[&_a]:underline',
    '[&_img]:my-2',
    '[&_table]:my-2 [&_table]:text-xs',
    '[&_th]:px-2 [&_th]:py-1',
    '[&_td]:px-2 [&_td]:py-1',
    '[&_hr]:my-3',
  ].join(' '),
  article: [
    'text-base leading-[1.75]',
    // h1 在 wiki 正文里基本不出现（标题是页面级的 <h1>），但正文里真写了一个
    // 也不该比 h2 小。
    '[&_h1]:text-[30px] [&_h1]:font-bold [&_h1]:leading-tight [&_h1]:mt-10 [&_h1]:mb-4',
    '[&_h2]:text-[24px] [&_h2]:font-semibold [&_h2]:leading-[1.3] [&_h2]:mt-9 [&_h2]:mb-3',
    '[&_h3]:text-[19px] [&_h3]:font-semibold [&_h3]:leading-[1.35] [&_h3]:mt-7 [&_h3]:mb-2.5',
    // 顶条是 sticky 的（45px）。不给标题留出滚动外边距，点目录跳过去时标题
    // 会正好被顶条盖住 —— 看起来像跳错了地方。
    '[&_h2]:scroll-mt-16 [&_h3]:scroll-mt-16',
    '[&_p]:my-4',
    '[&_ul]:pl-6 [&_ol]:pl-6 [&_li]:my-1.5',
    // 行内 code：设计稿给了自己的字色，并把字号压到 .87em（等宽字在同样字号
    // 下看起来比正文大一号）。
    '[&_code]:px-1.5 [&_code]:py-px [&_code]:text-[0.87em] [&_code]:text-wiki-code',
    '[&_pre]:p-4 [&_pre]:rounded-lg [&_pre]:border [&_pre]:border-todo-line',
    '[&_pre_code]:text-[13px] [&_pre_code]:leading-relaxed [&_pre_code]:text-todo-ink2',
    '[&_blockquote]:border-l-[3px] [&_blockquote]:pl-3.5 [&_blockquote]:text-todo-ink2',
    // 长文里的链接不用下划线：一段话里三个下划线会把行距搅乱。改成淡一档的
    // 下边框（设计稿的 .body a），可点性还在，但不再切断行。
    '[&_a]:no-underline [&_a]:border-b [&_a]:border-todo-accent/40 [&_a]:hover:border-todo-accent',
    '[&_img]:my-5 [&_img]:rounded-lg [&_img]:border [&_img]:border-todo-line',
    '[&_table]:my-5 [&_table]:text-[14.5px]',
    '[&_th]:px-2.5 [&_th]:py-[7px] [&_th]:align-top',
    '[&_td]:px-2.5 [&_td]:py-[7px] [&_td]:align-top',
    '[&_hr]:my-8',
  ].join(' '),
} as const;

type MarkdownProps = {
  children: string;
  className?: string;
  size?: keyof typeof SIZES;
};

// 标题的锚点 id。id 由**源码行号**算，算法在 utils/wikiHeadings.ts —— 目录那侧
// 用同一个函数，两边才跳得准（那个文件的注释论证了为什么不是计数器、不是
// rehype-slug）。
//
// 拿不到 position 时不挂 id：那一节就是不可跳转，而不是所有节都挂上同一个 id。
const anchorFor = (node: unknown) => {
  const line = (node as { position?: { start?: { line?: number } } })?.position?.start?.line;
  return line ? { id: headingId(line) } : {};
};

const Markdown: React.FC<MarkdownProps> = ({ children, className, size = 'compact' }) => (
  <div className={[SIZES[size], BASE, className].filter(Boolean).join(' ')}>
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      components={{
        // node 要摘掉 —— 它是 react-markdown 的内部结构，透传给 DOM 元素会变成
        // 一个 React 不认识的属性并告警。
        a: ({ node: _node, href, ...props }) => (
          <a
            {...props}
            href={href}
            // 站内链接留在当前标签页：wiki 正文里的「相关」一节指向的是别的
            // wiki 页面，那一跳是**继续读**，不是离开。站外仍然新开 ——
            // 读到一半被外链顶掉当前页是另一回事。
            //
            // 判据是「以 / 开头」而不是解析 origin：正文里的相对链接只有这一种
            // 写法（图片是 /img/:key，页面是 /wiki/:url_key），而
            // `//evil.com` 这种协议相对的写法会被 startsWith('//') 排掉。
            {...(href?.startsWith('/') && !href.startsWith('//')
              ? {}
              : { target: '_blank', rel: 'noreferrer' })}
          />
        ),
        h2: ({ node, children: kids, ...props }) => (
          <h2 {...props} {...anchorFor(node)}>
            {kids}
          </h2>
        ),
        h3: ({ node, children: kids, ...props }) => (
          <h3 {...props} {...anchorFor(node)}>
            {kids}
          </h3>
        ),
      }}
    >
      {children}
    </ReactMarkdown>
  </div>
);

export default Markdown;
